import { useState, useEffect, FC, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import clsx from 'clsx';

import { IItemProps } from '../../../ProductPage/components/List/List';

import { useTypedSelector } from '../../../../../lib/hooks/useTypedSelector';
import Settings from '../../../../Icons/Settings';
import Arrow from '../../../../Icons/Arrow';
import Status from '../../../../Status/Status';
import Cart from '../../../ProductPage/Cart';
import Comment from '../../../../Icons/Comment';
import ModalAddToCart from '../../../../ModalAddToCart/ModalAddToCart';
import ModalComment from '../../../../ModalComment/ModalComment';

import { getDocumentUrl } from '../../../../../api/document/getDocumentUrl';

import { formatBits } from '../../../../../helpers/formatBits';

import styles from '../../SchemePage.module.scss';

const Item: FC<IItemProps> = ({
  name,
  article_number,
  cmmf,
  type,
  status,
  commercial_number,
  explodedViews = [],
  repInstruction = [],
  electricalScheme = [],
  techInfo = [],
  id,
  image,
  className,
  replacedBy,
  metaData,
}) => {
  const navigate = useNavigate();

  const { cart } = useTypedSelector((state) => state);
  const indexInCart = cart.findIndex((item) => item.product_id === id);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const modalRef = useRef<HTMLDivElement | null>(null);

  const [isModalActive, setModalActive] = useState(false);

  const handleModalOpen = () => {
    setModalActive(true);
    document.body.style.overflow = 'hidden';
  };
  const handleModalClose = () => {
    setModalActive(false);
    document.body.style.overflow = 'auto';
  };

  const [commentVisible, setCommentVisible] = useState(false);
  const [isModalCommentActive, setModalCommentActive] = useState(false);
  const handleModalCommentOpen = () => {
    setModalCommentActive(true);
  };
  const handleModalCommentClose = () => {
    setModalCommentActive(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node))
        setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [electricalSchemeLinks, setElectricalSchemeLinks] = useState<
    { url: string; size: number }[]
  >([]);
  const [repInstructionLinks, setRepInstructionLinks] = useState<
    { url: string; size: number }[]
  >([]);
  const [techInfoLinks, setTechInfoLinks] = useState<
    { url: string; size: number }[]
  >([]);
  const [explodedViewsLink, setExplodedViewsLink] = useState<null | string>(
    null,
  );

  useEffect(() => {
    if (electricalScheme.length) {
      electricalScheme.forEach((item) => {
        getDocumentUrl(item.s3Bucket, item.s3Key).then((res) => {
          setElectricalSchemeLinks((state) => [
            ...state,
            { url: res.url, size: item.size },
          ]);
        });
      });
    }

    if (repInstruction.length) {
      repInstruction.forEach((item) => {
        getDocumentUrl(item.s3Bucket, item.s3Key).then((res) => {
          setRepInstructionLinks((state) => [
            ...state,
            { url: res.url, size: item.size },
          ]);
        });
      });
    }

    if (techInfo.length) {
      techInfo.forEach((item) => {
        getDocumentUrl(item.s3Bucket, item.s3Key).then((res) => {
          setTechInfoLinks((state) => [
            ...state,
            { url: res.url, size: item.size },
          ]);
        });
      });
    }

    if (explodedViews && explodedViews.length && explodedViews[0]) {
      if (!explodedViews[0].s3Bucket || !explodedViews[0].s3Key) return;
      getDocumentUrl(explodedViews[0].s3Bucket, explodedViews[0].s3Key)
        .then((res) => {
          setExplodedViewsLink(res.url);
        })
        .catch(() => {
          setExplodedViewsLink(null);
        });
    }
  }, [electricalScheme, repInstruction, techInfo, explodedViews]);

  let replacedByItem =
    replacedBy?.length && Array.isArray(replacedBy)
      ? replacedBy.filter(
          (i) =>
            i.displayData.status &&
            i.displayData.status !== 'discontinued' &&
            i.displayData.isActive,
        )[0] || null
      : null;

  const in_stoke = status !== 'discontinued';

  return (
    <>
      <Link
        to={'/product/' + id}
        state={{ type: type }}
        className={clsx(styles.Item, className ? className : '')}
      >
        {type === 'model' ? (
          <>
            <span>{article_number}</span>
            <span>{commercial_number}</span>
            <span>{name}</span>
            <div>
              <div
                className={clsx(styles.select, { [styles.open]: isOpen })}
                ref={modalRef}
              >
                {electricalSchemeLinks.length ||
                repInstructionLinks.length ||
                techInfoLinks.length ||
                explodedViewsLink ? (
                  <>
                    <span
                      className={styles.button}
                      onClick={(e) => {
                        e.preventDefault();
                        isOpen ? setIsOpen(false) : setIsOpen(true);
                      }}
                    >
                      <Settings />
                    </span>
                    <div className={styles.options}>
                      <ul>
                        {electricalSchemeLinks.map((item) => (
                          <li key={item.url}>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(item.url, '_blank');
                              }}
                            >
                              Взрыв-схема.pdf{' '}
                              <span>({formatBits(item.size)})</span>
                            </button>
                          </li>
                        ))}
                        {repInstructionLinks.map((item) => (
                          <li key={item.url}>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(item.url, '_blank');
                              }}
                            >
                              Руководство по ремонту.pdf{' '}
                              <span>({formatBits(item.size)})</span>
                            </button>
                          </li>
                        ))}
                        {techInfoLinks.map((item) => (
                          <li key={item.url}>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(item.url, '_blank');
                              }}
                            >
                              Tехническая информация.pdf{' '}
                              <span>({formatBits(item.size)})</span>
                            </button>
                          </li>
                        ))}
                        {explodedViewsLink && (
                          <li>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                window.open(explodedViewsLink, '_blank');
                              }}
                            >
                              Номенклатура.pdf <span>(672 КБ)</span>
                            </button>
                          </li>
                        )}
                        {explodedViews.length &&
                          explodedViews[0].s3Bucket &&
                          explodedViews[0].s3Key && (
                            <a
                              className={styles.link_schema}
                              href={'/scheme/' + id}
                              onClick={(e) => {
                                e.preventDefault();
                                window.open('/scheme/' + id);
                              }}
                            >
                              Интерактивная взрыв-схема
                              <Arrow />
                            </a>
                          )}
                      </ul>
                    </div>
                  </>
                ) : null}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.type}>
              <img
                src={
                  type === 'model'
                    ? '/icons/model.png'
                    : type === 'part'
                      ? '/icons/detail.png'
                      : '/icons/accessory.png'
                }
                alt={type}
              />
            </div>
            <span className={styles.article}>
              <img
                src={
                  type === 'model'
                    ? '/icons/model.png'
                    : type === 'part'
                      ? '/icons/detail.png'
                      : '/icons/accessory.png'
                }
                alt={type}
                className={styles.type_mob}
              />
              {article_number}
            </span>
            <span className={styles.name}>{name}</span>
            <div className={styles.status_holder}>
              {status && <Status status={status || ''} list={true} />}
            </div>
            <div className={styles.button_holder_item}>
              {indexInCart === -1 ? (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    handleModalOpen();
                  }}
                  className={clsx({
                    [styles.no_stoke]:
                      !in_stoke && !replacedByItem ? true : false,
                  })}
                  disabled={!in_stoke && !replacedByItem ? true : false}
                >
                  <Cart />
                </button>
              ) : (
                <div className={styles.cart_holder}>
                  <button
                    className={clsx(styles.cart_btn)}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/cart');
                    }}
                  >
                    {cart[indexInCart].count} <Cart />
                  </button>
                  {cart[indexInCart].comment && (
                    <div
                      className={clsx(styles.comment, {
                        [styles.open]: commentVisible,
                      })}
                      onPointerEnter={() => setCommentVisible(true)}
                      onPointerLeave={() => setCommentVisible(false)}
                      onClick={() => handleModalCommentOpen()}
                    >
                      <Comment />
                      <p>{cart[indexInCart].comment}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </Link>
      {isModalCommentActive && (
        <ModalComment
          onClose={handleModalCommentClose}
          comment={cart[indexInCart].comment || ''}
        />
      )}
      {isModalActive && (
        <ModalAddToCart
          onClose={handleModalClose}
          data={{
            available:
              !metaData?.count && replacedByItem
                ? replacedByItem.metaData.count
                : metaData?.count,
            product_id:
              !metaData?.count && replacedByItem ? replacedByItem.id : id,
            name:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.name
                : name,
            status:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.status || ''
                : status || '',
            cmmf:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.cmmf
                : cmmf,
            article_number:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.partCode
                : article_number,
            type:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.type
                : type,
            image:
              !metaData?.count && replacedByItem
                ? replacedByItem.displayData.image?.s3Key || ''
                : image?.s3Key || '',
            count: 1,
          }}
        />
      )}
    </>
  );
};

export default Item;
